import styled, { css } from 'styled-components';

import { Container, ContentContainer } from '@components/atoms';
import { device } from '@styles/mediaQuery';

function PrivacyPolicyPageContent() {
  return (
    <PageContainer>
      <Content>
        <Header>
          <Eyebrow>GREEN LINE LOGISTICS, LLC</Eyebrow>
          <Title>Privacy Policy</Title>
          <Meta>
            Applies to greenlinelog.com and Green Line&apos;s platform and
            services <Separator>·</Separator> Effective July 10, 2026
          </Meta>
        </Header>

        <Introduction>
          This policy explains what information Green Line Logistics, LLC
          collects, how we use and protect it, who we share it with, how long we
          keep it, and the choices available to you. It applies to our website,
          our software platform, and the services we provide, including any
          third-party services you choose to connect.
        </Introduction>

        <Section>
          <SectionTitle>
            <Number>1.</Number> Who we are
          </SectionTitle>
          <Paragraph>
            Green Line Logistics, LLC (&ldquo;Green Line,&rdquo;
            &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates
            greenlinelog.com and provides transportation, logistics, and related
            software services (together, the &ldquo;Services&rdquo;). This
            Privacy Policy describes how we handle information in connection
            with the Services.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>2.</Number> Scope
          </SectionTitle>
          <Paragraph>
            This policy covers information handled through our website, our
            internal and customer-facing software platform, and our business
            operations. It does not cover the privacy practices of any third
            party whose services or websites you access, even where linked to or
            connected with our Services. Those third parties have their own
            policies.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>3.</Number> Information we collect
          </SectionTitle>

          <SubsectionTitle>A. Information you provide</SubsectionTitle>
          <Paragraph>
            Business and contact details such as company name, user name, email
            address, phone number, and any information you submit through forms,
            accounts, or communications with us.
          </Paragraph>

          <SubsectionTitle>
            B. Information from services you connect
          </SubsectionTitle>
          <Paragraph>
            Where you authorize a connection between our platform and a
            third-party service (for example, accounting, financial, or
            operational software), we access the data you authorize, on a
            read-only basis unless stated otherwise, and only to the extent
            needed to provide the requested functionality. We do not modify data
            in a connected service or take actions in it beyond what you
            authorize.
          </Paragraph>

          <SubsectionTitle>C. Technical and usage data</SubsectionTitle>
          <Paragraph>
            Server logs, IP addresses, device and browser information,
            timestamps, error reports, cookies and similar technologies, and
            records of how the Services are used.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>4.</Number> How we use information
          </SectionTitle>
          <List>
            <li>To provide, operate, and maintain the Services;</li>
            <li>
              To display, synchronize, and refresh information within the
              platform, including data from services you connect;
            </li>
            <li>To secure, troubleshoot, and improve the Services;</li>
            <li>
              To communicate with you about your account, support requests, and
              service updates; and
            </li>
            <li>
              To meet our accounting, audit, legal, and compliance obligations.
            </li>
          </List>
          <Paragraph>
            We do not sell your information, and we do not share it for
            anyone&rsquo;s advertising or marketing purposes.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>5.</Number> How we store information and where it is
            accessed
          </SectionTitle>
          <Paragraph>
            We store information, including data retrieved from connected
            services, in our own systems and with our hosting providers so it
            can be used within the platform. Green Line operates with a United
            States office and a Ukraine-based operations team. Authorized
            personnel and vetted contractors, including members of our
            Ukraine-based operations team, may access this information in the
            course of their work. Where information is accessed from outside the
            United States, we apply the same access controls and confidentiality
            obligations to that access.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>6.</Number> How we share information
          </SectionTitle>
          <Paragraph>
            We do not sell or rent your information. We disclose it only as
            follows:
          </Paragraph>
          <List>
            <li>
              <strong>Service providers.</strong> To hosting, infrastructure,
              and support vendors that operate our systems under contract and
              may use the information only to provide services to us.
            </li>
            <li>
              <strong>Connected third parties.</strong> To a third-party service
              you have connected, only as needed to maintain that connection and
              provide the functionality you requested.
            </li>
            <li>
              <strong>Legal and safety.</strong> Where required by law,
              subpoena, or legal process, or to protect our rights, our systems,
              or the security of the information.
            </li>
            <li>
              <strong>Corporate transactions.</strong> In connection with a
              merger, reorganization, financing, or transfer of assets among
              Green Line&rsquo;s affiliated entities, subject to this policy.
            </li>
          </List>
        </Section>

        <Section>
          <SectionTitle>
            <Number>7.</Number> Data retention and deletion
          </SectionTitle>
          <Paragraph>
            We keep information only as long as needed for the purposes
            described above or as required for legal, accounting, audit, or
            backup-retention purposes, after which it is deleted or anonymized.
            Where information is obtained through a connected third-party
            service and you disconnect that service, we stop retrieving further
            data from it and delete the associated stored data within thirty
            (30) days, except where a longer period is required for the purposes
            noted above.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>8.</Number> Managing connections and choices
          </SectionTitle>
          <Paragraph>
            You can disconnect a connected third-party service at any time from
            within the platform or from that third party&rsquo;s own settings.
            Disconnecting stops further data retrieval from that service and
            triggers deletion of the associated stored data as described in
            Section 7. You may also contact us to ask what information we hold
            about you or to request deletion, subject to our legal and
            record-keeping obligations.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>9.</Number> Security
          </SectionTitle>
          <Paragraph>
            We use commercially reasonable, industry-standard safeguards to
            protect information, including:
          </Paragraph>
          <List>
            <li>
              Encryption in transit (TLS) and encryption at rest for stored
              credentials and sensitive data;
            </li>
            <li>
              Role-based access controls limiting access to authorized personnel
              on a need-to-know basis;
            </li>
            <li>
              Secure handling of any authorization credentials or access tokens;
              and
            </li>
            <li>Logging and monitoring of platform activity.</li>
          </List>
          <Paragraph>
            No method of transmission or storage is completely secure, and we
            cannot guarantee absolute security.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>10.</Number> Cookies
          </SectionTitle>
          <Paragraph>
            Our website may use cookies and similar technologies to operate the
            site, remember preferences, and understand usage. You can control
            cookies through your browser settings; disabling some cookies may
            affect how the site functions.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>11.</Number> Children
          </SectionTitle>
          <Paragraph>
            The Services are business tools and are not directed to, or intended
            for use by, anyone under 18. We do not knowingly collect information
            from children.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>12.</Number> Changes to this policy
          </SectionTitle>
          <Paragraph>
            We may update this policy from time to time. Material changes will
            be posted on this page with a revised effective date. Continued use
            of the Services after an update constitutes acceptance of the
            updated policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>
            <Number>13.</Number> Contact us
          </SectionTitle>
          <ContactList>
            <li>Green Line Logistics, LLC</li>
            <li>Attn: Privacy / Compliance</li>
            <li>
              Email:{' '}
              <ContactLink href="mailto:privacy@greenlinelog.com">
                privacy@greenlinelog.com
              </ContactLink>
            </li>
            <li>
              Website:{' '}
              <ContactLink href="https://greenlinelog.com/">
                https://greenlinelog.com/
              </ContactLink>
            </li>
          </ContactList>
        </Section>
      </Content>
    </PageContainer>
  );
}

const PageContainer = styled(Container)`
  overflow: visible;
  padding-top: 116px;
  padding-bottom: 64px;

  ${device.tablet(css`
    padding-top: 148px;
    padding-bottom: 88px;
  `)}
`;

const Content = styled(ContentContainer)`
  max-width: 1040px;
`;

const Header = styled.header`
  margin-bottom: 28px;

  ${device.tablet(css`
    margin-bottom: 34px;
  `)}
`;

const Eyebrow = styled.p`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.colors.forestGreen};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 3px;
  text-transform: uppercase;

  ${device.tablet(css`
    font-size: 16px;
  `)}
`;

const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 38px;
  font-weight: 700;
  line-height: 44px;

  ${device.tablet(css`
    font-size: 52px;
    line-height: 60px;
  `)}
`;

const Meta = styled.p`
  margin: 14px 0 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 16px;
  line-height: 24px;

  ${device.tablet(css`
    font-size: 18px;
    line-height: 28px;
  `)}
`;

const Separator = styled.span`
  display: inline-block;
  margin: 0 6px;
`;

const Introduction = styled.p`
  margin: 0 0 16px;
  padding: 22px 20px;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-left: 4px solid ${({ theme }) => theme.colors.forestGreen};
  font-size: 17px;
  line-height: 27px;

  ${device.tablet(css`
    padding: 28px 30px;
    font-size: 19px;
    line-height: 30px;
  `)}
`;

const Section = styled.section`
  padding: 28px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};

  ${device.tablet(css`
    padding: 36px 0;
  `)}

  &:last-of-type {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;

  ${device.tablet(css`
    font-size: 30px;
    line-height: 38px;
  `)}
`;

const Number = styled.span`
  margin-right: 6px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SubsectionTitle = styled.h3`
  margin: 22px 0 6px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
`;

const Paragraph = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  line-height: 26px;

  ${device.tablet(css`
    font-size: 18px;
    line-height: 30px;
  `)}
`;

const List = styled.ul`
  margin: 14px 0;
  padding-left: 26px;
  list-style: disc;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  line-height: 26px;

  li {
    margin-bottom: 8px;
    padding-left: 4px;
  }

  li::marker {
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${device.tablet(css`
    padding-left: 34px;
    font-size: 18px;
    line-height: 30px;
  `)}
`;

const ContactList = styled.ul`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  line-height: 28px;

  ${device.tablet(css`
    font-size: 18px;
    line-height: 31px;
  `)}
`;

const ContactLink = styled.a`
  color: ${({ theme }) => theme.colors.forestGreen};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default PrivacyPolicyPageContent;
